import { Alert, Button } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { Attachment } from 'sr-types/lib/attachment/v1/graphql';
import { useEntitySecurityPath } from '../auth/Security';
import { BUTTON_VARIANTS } from '../constants/constants';
import { useDeepLinkURL } from '../deepLink';
import I18n from '../i18n/I18n';
import { H, ScrollableLayout, V } from '../Layout';
import { ShareSettings, shareSettingsDataToShareSettingsViewModel, useShareSettings } from '../share';
import { SHARE_SETTINGS_MODE } from '../share/constants';
import { failedToastMessage, successToastMessage } from '../utils/commonUtils';
import { makeExistingAttachmentShareSettingsScopeId } from './shareSettingsScope';

type ShareableAttachment = Attachment & {
    restricted?: boolean;
    sharedWithAccounts?: Parameters<typeof shareSettingsDataToShareSettingsViewModel>[0]['sharedWithAccounts'];
    sharedWithDepartments?: Parameters<typeof shareSettingsDataToShareSettingsViewModel>[0]['sharedWithDepartments'];
};

type AttachmentShareSettingsProps = {
    attachmentId: string | null;
    attachment: Attachment | null;
    onClose: () => void;
    onAfterShareSettingsSave?: () => Promise<unknown> | void;
};

export default function AttachmentShareSettings({
    attachmentId,
    attachment,
    onClose,
    onAfterShareSettingsSave
}: AttachmentShareSettingsProps) {
    const { saveShareSettings, checkHasUnsavedShareSettings, isSavingShareSettings } = useShareSettings();
    const { getDeepLinkURL } = useDeepLinkURL();
    const deepLinkUIStatePath = useEntitySecurityPath();

    const shareSettingsScopeId = useMemo(
        () => (attachmentId ? makeExistingAttachmentShareSettingsScopeId(attachmentId) : ''),
        [attachmentId]
    );

    const initialShareSettings = useMemo(() => {
        if (!attachment) return null;

        const shareableAttachment = attachment as ShareableAttachment;

        return shareSettingsDataToShareSettingsViewModel({
            restricted: Boolean(shareableAttachment.restricted),
            sharedWithAccounts: shareableAttachment.sharedWithAccounts ?? [],
            sharedWithDepartments: shareableAttachment.sharedWithDepartments ?? [],
            entityCreatedBy: shareableAttachment.createdBy,
            entityCreatedByName: shareableAttachment.createdByName ?? undefined
        });
    }, [attachment]);

    const hasUnsavedChanges = shareSettingsScopeId ? checkHasUnsavedShareSettings(shareSettingsScopeId) : false;

    const handleSave = useCallback(async () => {
        if (!attachmentId || !shareSettingsScopeId) {
            return;
        }

        try {
            const deepLinkURL = getDeepLinkURL(deepLinkUIStatePath, { id: attachmentId });

            await saveShareSettings(shareSettingsScopeId, { id: attachmentId }, deepLinkURL);
            successToastMessage('common.share.settings.update.success');
            await onAfterShareSettingsSave?.();
            onClose();
        } catch {
            failedToastMessage('common.share.settings.update.failure');
        }
    }, [
        attachmentId,
        getDeepLinkURL,
        onAfterShareSettingsSave,
        onClose,
        saveShareSettings,
        shareSettingsScopeId,
        deepLinkUIStatePath
    ]);

    if (!attachmentId || !attachment || !initialShareSettings) {
        return (
            <V gap={1} sx={{ justifyContent: 'center', alignItems: 'flex-end' }}>
                <Alert severity="error">
                    <I18n token="common.share.settings.update.failure" />
                </Alert>
                <Button variant={BUTTON_VARIANTS.text} onClick={onClose}>
                    <I18n token="dialog.back" />
                </Button>
            </V>
        );
    }

    return (
        <V data-testid={'shareSettings'} sx={{ maxHeight: '100%' }}>
            <ScrollableLayout sx={{ p: 1 }}>
                <ShareSettings
                    mode={SHARE_SETTINGS_MODE.update}
                    scopeId={shareSettingsScopeId}
                    initialShareSettings={initialShareSettings}
                />
            </ScrollableLayout>
            <H sx={{ justifyContent: 'flex-end', p: 1 }}>
                <Button
                    variant={BUTTON_VARIANTS.text}
                    sx={{ mr: 2 }}
                    onClick={onClose}
                    disabled={isSavingShareSettings}
                >
                    <I18n token="dialog.cancel" />
                </Button>
                <Button
                    variant={BUTTON_VARIANTS.contained}
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges || isSavingShareSettings}
                >
                    <I18n token="form.button.save" />
                </Button>
            </H>
        </V>
    );
}
